import { getConfiguredCriticalKeywords } from './complaintSmartScanSettings';

export type LookupType = 'loan' | 'order';
export type LookupMessageSeverity = 'success' | 'info' | 'error';
export type RiskLevel = 'Standard' | 'High' | 'Critical';
export type CaseStatus =
  | 'New'
  | 'In Process'
  | 'Pending Vendor Info'
  | 'Under Legal Review'
  | 'Validated - No Maint Hold'
  | 'Escalated'
  | 'Closed';

export type ComplaintIntakeData = {
  complaintId: string;
  rowVersion: string;
  orderNumber: string;
  propertyLoanId: string;
  workOrderId: string;
  loanNumber: string;
  spiPropertyId: string;
  loanDisplay: string;
  loanType: string;
  propertyAddress: string;
  city: string;
  state: string;
  zip: string;
  vendorCode: string;
  clientLabel: string;
  clientCode: string;
  investorLabel: string;
  complainantFirst: string;
  complainantLast: string;
  complainantType: string;
  complainantTypeCode: string;
  complainantTypeCodes?: string[];
  complainantContactEmail: string;
  complainantContactPhone: string;
  counselinkId: string;
  department: string;
  departmentCode: string;
  departmentCodes?: string[];
  severity: string;
  severityCode: string;
  severityCodes?: string[];
  workType: string;
  workTypeCode: string;
  complaintType: string;
  complaintTypeCode: string;
  complaintTypeCodes?: string[];
  contactSource: string;
  contactSourceCode: string;
  contactSourceCodes?: string[];
  receivedDate: string;
  description: string;
  demand: string;
  assignedTo: string;
};

export type ResolutionData = {
  followUpDate: string;
  actionSteps: string;
  resolutionDetails: string;
};

export type ActivityLogEntry = {
  id: number;
  date: string;
  type: string;
  user: string;
  note: string;
};

export type ComplaintCodeValue = {
  code?: string;
  Code?: string;
  displayName?: string;
  display_name?: string;
  description?: string;
  Description?: string;
  sortOrder?: number;
  sort_order?: number;
  active?: boolean;
  isActive?: boolean;
  is_active?: boolean;
};

export type ComplaintStatusTransition = {
  fromStatusCode?: string;
  from_status_code?: string;
  toStatusCode?: string;
  to_status_code?: string;
  displayName?: string;
  display_name?: string;
  sortOrder?: number;
  sort_order?: number;
  active?: boolean;
  isActive?: boolean;
  is_active?: boolean;
};

export type ComplaintLookupSet = {
  complaintTypes?: ComplaintCodeValue[];
  complainantTypes?: ComplaintCodeValue[];
  contactSources?: ComplaintCodeValue[];
  caseStatuses?: ComplaintCodeValue[];
  riskLevels?: ComplaintCodeValue[];
  departments?: ComplaintCodeValue[];
  severities?: ComplaintCodeValue[];
  activityTypes?: ComplaintCodeValue[];
  activityDirections?: ComplaintCodeValue[];
  statusTransitions?: ComplaintStatusTransition[];
  lookupGroups?: Record<string, ComplaintCodeValue[]>;
};

export type RiskIndicators = {
  police: boolean;
  media: boolean;
  safety: boolean;
  attorney: boolean;
};

export type RiskAssessment = {
  level: RiskLevel;
  indicators: RiskIndicators;
  configuredCritical?: boolean;
};

const riskLevelRank: Record<RiskLevel, number> = {
  Standard: 0,
  High: 1,
  Critical: 2,
};

const riskKeywordPatterns: Record<keyof RiskIndicators, RegExp[]> = {
  police: [
    /\bpolice\b/,
    /\bsheriff\b/,
    /\bdeput(?:y|ies)\b/,
    /\bofficers?\b/,
    /\blaw enforcement\b/,
    /\b911\b/,
    /\bcops?\b/,
  ],
  media: [
    /\bmedia\b/,
    /\bnews\b/,
    /\breporters?\b/,
    /\bjournalists?\b/,
    /\bpress\b/,
    /\btelevision\b/,
    /\btv\b/,
    /\bchannel(?:\s+\d+)?\b/,
    /\bnewspaper\b/,
  ],
  safety: [
    /\bweapons?\b/,
    /\bguns?\b/,
    /\bfirearms?\b/,
    /\bknives?\b/,
    /\bthreat\w*\b/,
    /\bunsafe\b/,
    /\bsafety\b/,
    /\bafraid\b/,
    /\bscared\b/,
    /\bintimidat\w*\b/,
    /\bassault\w*\b/,
    /\bconceal\w*\b/,
  ],
  attorney: [
    /\blegal\b/,
    /\blegal hold\b/,
    /\battorneys?\b/,
    /\blawyers?\b/,
    /\bcounsel\b/,
    /\blitigation\b/,
    /\blawsuits?\b/,
    /\bcourt\b/,
    /\bsu(?:e|ed|es|ing)\b/,
    /\bsubpoena\w*\b/,
    /\bliability\b/,
  ],
};

const hasRiskKeyword = (text: string, riskType: keyof RiskIndicators) =>
  riskKeywordPatterns[riskType].some((pattern) => pattern.test(text));

const hasConfiguredCriticalKeyword = (text: string) =>
  getConfiguredCriticalKeywords().some((keyword) => {
    const escaped = keyword.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);
    return new RegExp(`\\b${escaped}\\b`, 'i').test(text);
  });

export const getHighestRiskLevel = (...levels: Array<RiskLevel | undefined>): RiskLevel => {
  let highest: RiskLevel = 'Standard';

  for (const level of levels) {
    if (level && riskLevelRank[level] > riskLevelRank[highest]) {
      highest = level;
    }
  }

  return highest;
};

export type ComplaintMatterPayload = {
  propertyLoanId?: string;
  spiPropertyId?: string;
  orderNumber?: string;
  workOrderId?: string;
  complainantFirst: string;
  complainantLast: string;
  complainantTypeCode: string;
  complainantContactEmail?: string;
  complainantContactPhone?: string;
  departmentCode?: string;
  severityCode?: string;
  complaintTypeCode: string;
  contactSourceCode: string;
  receivedDate?: string;
  description: string;
  demand?: string;
  assignedToUser?: string;
  vendorCode?: string;
  caseStatusCode: string;
  riskLevelCode: string;
  riskIndicators: RiskIndicators;
  followUpDate?: string;
  actionSteps?: string;
  resolutionDetails?: string;
};

export type CreateComplaintMatterRequest = {
  matter: ComplaintMatterPayload;
};

export type UpdateComplaintMatterRequest = {
  rowVersion: string;
  matter: ComplaintMatterPayload;
};

export type ComplaintFormState = {
  formData: ComplaintIntakeData;
  resolutionData: ResolutionData;
  caseStatus: CaseStatus;
  riskLevel?: RiskLevel;
  activityLog: ActivityLogEntry[];
  additionalFieldValues?: Record<string, string | string[]>;
};

export type FieldRecord = Record<string, unknown>;

export const isFieldRecord = (value: unknown): value is FieldRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const textFromValue = (value: unknown) => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const optionalText = (value: unknown) => textFromValue(value) || undefined;

const optionalId = (value: unknown) => {
  const text = textFromValue(value);
  if (!text) return;

  return text;
};

export const initialData: ComplaintIntakeData = {
  complaintId: '',
  rowVersion: '',
  orderNumber: '',
  propertyLoanId: '',
  workOrderId: '',
  loanNumber: '',
  spiPropertyId: '',
  loanDisplay: '',
  loanType: '',
  propertyAddress: '',
  city: '',
  state: '',
  zip: '',
  vendorCode: '',
  clientLabel: '',
  clientCode: '',
  investorLabel: '',
  complainantFirst: '',
  complainantLast: '',
  complainantType: '',
  complainantTypeCode: '',
  complainantContactEmail: '',
  complainantContactPhone: '',
  counselinkId: '',
  department: '',
  departmentCode: '',
  severity: 'Complaint',
  severityCode: 'COMPLAINT',
  workType: '',
  workTypeCode: '',
  complaintType: '',
  complaintTypeCode: '',
  complaintTypeCodes: [],
  contactSource: '',
  contactSourceCode: '',
  receivedDate: '',
  description: '',
  demand: '',
  assignedTo: '',
};

export const initialResolutionData: ResolutionData = {
  followUpDate: '',
  actionSteps: '',
  resolutionDetails: '',
};

export const caseStatusOptions: CaseStatus[] = [
  'New',
  'In Process',
  'Pending Vendor Info',
  'Under Legal Review',
  'Validated - No Maint Hold',
  'Escalated',
  'Closed',
];

export const initialActivityLog: ActivityLogEntry[] = [];

export const emptyComplaintLookups: ComplaintLookupSet = {
  complaintTypes: [],
  complainantTypes: [],
  contactSources: [],
  caseStatuses: [],
  riskLevels: [],
  departments: [],
  severities: [],
  activityTypes: [],
  activityDirections: [],
  statusTransitions: [],
  lookupGroups: {},
};

const requiredComplaintFields: {
  key: keyof ComplaintIntakeData;
  codeKey?: keyof ComplaintIntakeData;
  label: string;
}[] = [
  { key: 'complainantFirst', label: 'Complainant first name' },
  { key: 'complainantLast', label: 'Complainant last name' },
  {
    key: 'complainantType',
    codeKey: 'complainantTypeCode',
    label: 'Complainant type',
  },
  {
    key: 'contactSource',
    codeKey: 'contactSourceCode',
    label: 'Contact source',
  },
  { key: 'receivedDate', label: 'Received date' },
  {
    key: 'complaintType',
    codeKey: 'complaintTypeCode',
    label: 'Complaint type',
  },
  { key: 'description', label: 'Incident narrative' },
];

export const hasSafeViewContext = (formData: ComplaintIntakeData) =>
  Boolean(
    formData.spiPropertyId.trim() || formData.propertyLoanId.trim() || formData.orderNumber.trim()
  );

export const getMissingComplaintFields = (formData: ComplaintIntakeData) => {
  const missingFields = requiredComplaintFields
    .filter(
      (field) =>
        !textFromValue(formData[field.key]).trim() &&
        !(field.codeKey && textFromValue(formData[field.codeKey]).trim())
    )
    .map((field) => field.label);

  return hasSafeViewContext(formData) ? missingFields : ['SafeView record', ...missingFields];
};

export const clearSafeViewContext = (data: ComplaintIntakeData): ComplaintIntakeData => ({
  ...data,
  complaintId: '',
  rowVersion: '',
  orderNumber: '',
  propertyLoanId: '',
  workOrderId: '',
  loanNumber: '',
  spiPropertyId: '',
  loanDisplay: '',
  loanType: '',
  propertyAddress: '',
  city: '',
  state: '',
  zip: '',
  vendorCode: '',
  clientLabel: '',
  clientCode: '',
  investorLabel: '',
});

const toUpperSnake = (value: string) =>
  value
    .trim()
    .replaceAll('&', ' and ')
    .replaceAll(/[^\dA-Za-z]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
    .toUpperCase();

const codeFromDisplay = (
  explicitCode: string,
  displayValue: string,
  knownCodes: Record<string, string>
) => {
  const explicit = optionalText(explicitCode);
  if (explicit) return explicit;

  const display = displayValue.trim();
  if (!display) return '';

  return knownCodes[display.toLowerCase()] || toUpperSnake(display);
};

export const lookupDisplay = (value?: ComplaintCodeValue) =>
  textFromValue(value?.displayName) ||
  textFromValue(value?.display_name) ||
  textFromValue(value?.code) ||
  textFromValue(value?.Code);

export const lookupCode = (value?: ComplaintCodeValue) =>
  textFromValue(value?.code) || textFromValue(value?.Code);

export const activeLookupValues = (values: ComplaintCodeValue[] = []) =>
  values.filter((value) => {
    if (!value || typeof value !== 'object') return false;

    const active = value.active ?? value.isActive ?? value.is_active ?? true;
    return active !== false && lookupCode(value);
  });

export const hasActiveLookupCode = (values: ComplaintCodeValue[] = [], code: string) => {
  const normalizedCode = code.trim().toUpperCase();
  if (!normalizedCode) return false;

  return activeLookupValues(values).some(
    (value) => lookupCode(value).toUpperCase() === normalizedCode
  );
};

const SYSTEM_ACTIVITY_TYPE_PREFIX = 'SYSTEM';

const isSystemActivityType = (value?: ComplaintCodeValue) => {
  const normalizedCode = toUpperSnake(lookupCode(value));
  const normalizedDisplay = toUpperSnake(lookupDisplay(value));

  return (
    normalizedCode === SYSTEM_ACTIVITY_TYPE_PREFIX ||
    normalizedCode.startsWith(`${SYSTEM_ACTIVITY_TYPE_PREFIX}_`) ||
    normalizedDisplay === SYSTEM_ACTIVITY_TYPE_PREFIX ||
    normalizedDisplay.startsWith(`${SYSTEM_ACTIVITY_TYPE_PREFIX}_`)
  );
};

export const userCreatableActivityTypes = (values: ComplaintCodeValue[] = []) =>
  activeLookupValues(values).filter((value) => !isSystemActivityType(value));

export const hasUserCreatableActivityTypeCode = (
  values: ComplaintCodeValue[] = [],
  code: string
) => {
  const normalizedCode = code.trim().toUpperCase();
  if (!normalizedCode) return false;

  return userCreatableActivityTypes(values).some(
    (value) => lookupCode(value).toUpperCase() === normalizedCode
  );
};

export const displayForLookupCode = (values: ComplaintCodeValue[] = [], code: string) => {
  const normalizedCode = code.trim().toUpperCase();
  if (!normalizedCode) return '';

  return (
    lookupDisplay(
      values.find((value) => lookupCode(value).toUpperCase() === normalizedCode) || {}
    ) || ''
  );
};

export const codeForLookupDisplay = (values: ComplaintCodeValue[] = [], display: string) => {
  const normalizedDisplay = display.trim().toLowerCase();
  if (!normalizedDisplay) return '';

  return (
    lookupCode(
      values.find((value) => lookupDisplay(value).toLowerCase() === normalizedDisplay) || {}
    ) || ''
  );
};

const complaintTypeCodeByDisplay: Record<string, string> = {
  'do no work': 'DO_NOT_WORK',
  other: 'OTHER',
};

const complainantTypeCodeByDisplay: Record<string, string> = {
  mortgagor: 'MORTGAGOR',
  tenant: 'TENANT',
  neighbor: 'NEIGHBOR',
  vendor: 'VENDOR',
  other: 'OTHER',
};

const contactSourceCodeByDisplay: Record<string, string> = {
  call: 'CALL',
  email: 'EMAIL',
  'e-mail': 'EMAIL',
  letter: 'LETTER',
  'mail (letter)': 'MAIL_LETTER',
  'social media': 'SOCIAL_MEDIA',
  'email - stericycle': 'EMAIL_STERICYCLE',
  portal: 'PORTAL',
  client: 'CLIENT',
  other: 'OTHER',
};

const caseStatusCodeByDisplay: Record<string, string> = {
  new: 'NEW',
  'in process': 'IN_PROCESS',
  'pending vendor info': 'PENDING_VENDOR_INFO',
  'under legal review': 'UNDER_LEGAL_REVIEW',
  'validated - no maint hold': 'VALIDATED_NO_MAINT_HOLD',
  escalated: 'ESCALATED',
  closed: 'CLOSED',
};

const departmentCodeByDisplay: Record<string, string> = {
  iso: 'ISO',
  'insurance loss': 'INSURANCE_LOSS',
  'p&p': 'P_AND_P',
  reo: 'REO',
};

const severityCodeByDisplay: Record<string, string> = {
  advisement: 'ADVISEMENT',
  complaint: 'COMPLAINT',
};

const riskLevelCodeByDisplay: Record<string, string> = {
  standard: 'STANDARD',
  high: 'HIGH',
  critical: 'CRITICAL',
};

const displayByCode = (code: string, values: Record<string, string>) => {
  const normalizedCode = code.trim().toUpperCase();
  if (!normalizedCode) return '';

  const match = Object.entries(values).find(([, value]) => value === normalizedCode);

  return match?.[0] ? match[0].replaceAll(/\b\w/g, (letter) => letter.toUpperCase()) : '';
};

const activityTypeDisplayByCode: Record<string, string> = {
  call: 'CALL',
  note: 'NOTE',
  'system note': 'SYSTEM_NOTE',
  escalation: 'ESCALATION',
  'outbound email': 'OUTBOUND_EMAIL',
};

const dateFromTimestamp = (value: unknown) => {
  const text = textFromValue(value);
  if (!text) return '';

  const dateOnly = text.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (dateOnly) return dateOnly;

  const timestamp = Date.parse(text);
  if (Number.isNaN(timestamp)) return text.slice(0, 10);

  return new Date(timestamp).toISOString().slice(0, 10);
};

const parseMetadata = (value: unknown): FieldRecord => {
  if (isFieldRecord(value)) return value;

  const text = textFromValue(value);
  if (!text) return {};

  try {
    const parsed = JSON.parse(text);
    return isFieldRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const normalizeLoanType = (value: unknown) => {
  const text = textFromValue(value);
  const parts = text.split('.');

  return parts.at(-1) || text;
};

const normalizeAddressForComparison = (value: string) =>
  value
    .replaceAll(/[\s,]+/g, ' ')
    .trim()
    .toLowerCase();

const combineAddressLines = (line1: string, line2: string) => {
  const first = line1.trim();
  const second = line2.trim();
  if (!first) return second;
  if (!second) return first;

  const normalizedFirst = normalizeAddressForComparison(first);
  const normalizedSecond = normalizeAddressForComparison(second);

  if (normalizedFirst.includes(normalizedSecond)) return first;
  if (normalizedSecond.includes(normalizedFirst)) return second;
  return `${first}, ${second}`;
};

const mergeFormData = (
  base: ComplaintIntakeData,
  next: Partial<ComplaintIntakeData>
): ComplaintIntakeData => {
  const merged = { ...base };

  for (const [key, value] of Object.entries(next)) {
    if (Array.isArray(value) || textFromValue(value)) {
      merged[key as keyof ComplaintIntakeData] = value as never;
    }
  }

  return merged;
};

// Only overlay fields users edit on intake; SafeView context/routing fields stay backend-owned.
const draftManagedFields: Array<keyof ComplaintIntakeData> = [
  'complainantFirst',
  'complainantLast',
  'complainantType',
  'complainantTypeCode',
  'complainantContactEmail',
  'complainantContactPhone',
  'counselinkId',
  'department',
  'departmentCode',
  'severity',
  'severityCode',
  'complaintType',
  'complaintTypeCode',
  'complaintTypeCodes',
  'contactSource',
  'contactSourceCode',
  'receivedDate',
  'description',
  'demand',
  'assignedTo',
];

export const mergeDraftMatterFields = (
  base: ComplaintIntakeData,
  draft: ComplaintIntakeData
): ComplaintIntakeData => {
  const merged = { ...base };

  for (const field of draftManagedFields) {
    if (field === 'receivedDate' && base.receivedDate && !draft.receivedDate) {
      continue;
    }
    merged[field] = draft[field] as never;
  }

  return merged;
};

export const toProtobufTimestamp = (value: string) => {
  const text = value.trim();
  if (!text) return;

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text}T00:00:00Z`;
  }

  const timestamp = Date.parse(text);
  return Number.isNaN(timestamp) ? undefined : new Date(timestamp).toISOString();
};

export const getRiskAssessment = (description: string): RiskAssessment => {
  const text = description.toLowerCase();
  const configuredCritical = hasConfiguredCriticalKeyword(text);
  const indicators: RiskIndicators = {
    police: hasRiskKeyword(text, 'police'),
    media: hasRiskKeyword(text, 'media'),
    safety: hasRiskKeyword(text, 'safety'),
    attorney: hasRiskKeyword(text, 'attorney'),
  };

  if (configuredCritical || indicators.media || indicators.attorney) {
    return { level: 'Critical', indicators, configuredCritical };
  }

  if (indicators.police || indicators.safety) {
    return { level: 'High', indicators, configuredCritical };
  }

  return { level: 'Standard', indicators, configuredCritical };
};

export const getRiskColor = (level: RiskLevel) => {
  if (level === 'Critical') return '#c62828';
  if (level === 'High') return '#ef6c00';
  return '#1e63d6';
};

export const placeholderText = 'Not available';

export const displayText = (value: unknown, placeholder = placeholderText) => {
  return textFromValue(value) || placeholder;
};

export const firstValue = (record: FieldRecord | undefined, keys: string[]) => {
  if (!record) return '';

  for (const key of keys) {
    const value = textFromValue(record[key]);
    if (value) {
      return value;
    }
  }

  return '';
};

export const firstValueFromRecords = (records: (FieldRecord | undefined)[], keys: string[]) => {
  for (const record of records) {
    const value = firstValue(record, keys);
    if (value) return value;
  }

  return '';
};

const recordContainerKeys = [
  'answers',
  'Answers',
  'propertyLoans',
  'PropertyLoans',
  'property_loans',
  'results',
  'Results',
  'response',
  'Response',
  'items',
  'Items',
  'data',
  'Data',
];

export const getRecords = (payload: unknown): FieldRecord[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.filter((item) => isFieldRecord(item));
  if (!isFieldRecord(payload)) return [];

  const containers = recordContainerKeys.map((key) => payload[key]);

  for (const container of containers) {
    if (Array.isArray(container)) return container.filter((item) => isFieldRecord(item));
  }

  for (const container of containers) {
    if (!isFieldRecord(container)) continue;

    const nestedRecords = getRecords(container);
    if (
      nestedRecords.length > 0 &&
      !(nestedRecords.length === 1 && nestedRecords[0] === container)
    ) {
      return nestedRecords;
    }

    return [container];
  }

  return [payload];
};

const hasMergeValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  if (isFieldRecord(value)) return Object.keys(value).length > 0;
  return Boolean(textFromValue(value));
};

export const mergeRecords = (...records: (FieldRecord | undefined)[]) => {
  const merged: FieldRecord = {};

  for (const record of records) {
    for (const [key, value] of Object.entries(record || {})) {
      if (hasMergeValue(value) && !hasMergeValue(merged[key])) {
        merged[key] = value;
      }
    }
  }

  return merged;
};

const getSortableDate = (record: FieldRecord) => {
  const dateValue = firstValue(record, [
    'openDate',
    'OpenDate',
    'updateDate',
    'UpdateDate',
    'dueDate',
    'DueDate',
  ]);
  const timestamp = Date.parse(dateValue);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getSortableOrderNumber = (record: FieldRecord) => {
  const orderNumber = Number(
    firstValue(record, ['orderNumber', 'OrderNumber', 'workOrderNumber', 'WorkOrderNumber'])
  );

  return Number.isFinite(orderNumber) ? orderNumber : 0;
};

export const selectPrimaryWorkOrderRecord = (records: FieldRecord[]) => {
  if (records.length === 0) return;

  return [...records].sort((firstRecord, secondRecord) => {
    const dateDifference = getSortableDate(secondRecord) - getSortableDate(firstRecord);

    if (dateDifference !== 0) return dateDifference;

    return getSortableOrderNumber(secondRecord) - getSortableOrderNumber(firstRecord);
  })[0];
};

export const buildLoanContextLabel = (formData: ComplaintIntakeData) => {
  if (formData.loanDisplay.trim()) return 'Loan:';
  if (formData.propertyLoanId.trim()) return 'Property Loan:';
  if (formData.loanType.trim()) return 'Loan Type:';
  return 'Loan:';
};

export const buildLoanDisplay = (loanDisplay: string, loanType: string, propertyLoanId = '') => {
  const safeLoanDisplay = loanDisplay.trim() || propertyLoanId.trim();
  const normalizedLoanType = loanType.trim();

  if (!safeLoanDisplay) return normalizedLoanType || placeholderText;
  return normalizedLoanType ? `${safeLoanDisplay} (${normalizedLoanType})` : safeLoanDisplay;
};

export const buildAddressDisplay = (formData: ComplaintIntakeData) => {
  const state = formData.state.trim();
  const zip = formData.zip.trim();
  const localityTailParts = [state, zip]
    .filter(Boolean)
    .map((part) => part.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`));
  const localityTailPattern =
    localityTailParts.length > 0
      ? new RegExp(`,?\\s*(?:${localityTailParts.join('|')})\\b.*$`, 'i')
      : undefined;
  const city = formData.city
    .replaceAll(/\s+/g, ' ')
    .replace(localityTailPattern || /$/g, '')
    .trim();
  const parts = [city, state, zip].map((part) => part.trim()).filter(Boolean);
  const escapedParts = parts.map((part) => part.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`));
  const trailingLocalityPattern =
    escapedParts.length > 0
      ? new RegExp(`(?:,?\\s*(?:${escapedParts.join('|')}))*\\s*$`, 'i')
      : undefined;
  const street = formData.propertyAddress
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/\b([A-Z]{2})\s+\1\b/g, '$1')
    .replace(trailingLocalityPattern || /$/g, '')
    .replaceAll(/[\s,]+$/g, '')
    .trim();
  const addressParts = [street, ...parts].filter(Boolean);

  return addressParts.length > 0 ? addressParts.join(', ') : placeholderText;
};

export const buildSafeViewLinkAddress = (formData: ComplaintIntakeData) =>
  [formData.propertyAddress, formData.city, formData.state]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ') || placeholderText;

export const buildComplainantDisplay = (
  firstName: string,
  lastName: string,
  complainantType: string
) => {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  if (!name) return placeholderText;
  return complainantType.trim() ? `${name} (${complainantType})` : name;
};

export const mapSafeViewRecordToFormData = (record: FieldRecord): ComplaintIntakeData => {
  const workOrderRecord = isFieldRecord(record.workOrder)
    ? record.workOrder
    : isFieldRecord(record.WorkOrder)
      ? record.WorkOrder
      : isFieldRecord(record.work_order)
        ? record.work_order
        : undefined;
  const propertyLoanRecord = isFieldRecord(record.propertyLoan)
    ? record.propertyLoan
    : isFieldRecord(record.PropertyLoan)
      ? record.PropertyLoan
      : isFieldRecord(record.property_loan)
        ? record.property_loan
        : undefined;
  const propertyLoanSpiPropertyRecord = isFieldRecord(propertyLoanRecord?.spiProperty)
    ? propertyLoanRecord?.spiProperty
    : isFieldRecord(propertyLoanRecord?.SPIProperty)
      ? propertyLoanRecord?.SPIProperty
      : isFieldRecord(propertyLoanRecord?.SpiProperty)
        ? propertyLoanRecord?.SpiProperty
        : isFieldRecord(propertyLoanRecord?.sPIProperty)
          ? propertyLoanRecord?.sPIProperty
          : isFieldRecord(propertyLoanRecord?.sPI_property)
            ? propertyLoanRecord?.sPI_property
            : isFieldRecord(propertyLoanRecord?.spi_property)
              ? propertyLoanRecord?.spi_property
              : undefined;
  const propertyRecord = isFieldRecord(record.spiProperty)
    ? record.spiProperty
    : isFieldRecord(record.SPIProperty)
      ? record.SPIProperty
      : isFieldRecord(record.SpiProperty)
        ? record.SpiProperty
        : isFieldRecord(record.sPIProperty)
          ? record.sPIProperty
          : isFieldRecord(record.sPI_property)
            ? record.sPI_property
            : isFieldRecord(record.spi_property)
              ? record.spi_property
              : propertyLoanSpiPropertyRecord;
  const vendorRecord = isFieldRecord(record.vendor)
    ? record.vendor
    : isFieldRecord(record.Vendor)
      ? record.Vendor
      : isFieldRecord(workOrderRecord?.vendor)
        ? workOrderRecord?.vendor
        : isFieldRecord(workOrderRecord?.Vendor)
          ? workOrderRecord?.Vendor
          : undefined;
  const recordSources = [workOrderRecord, record, propertyLoanRecord];
  const orderNumber = firstValueFromRecords(recordSources, [
    'orderNumber',
    'OrderNumber',
    'order_number',
    'workOrderNumber',
    'WorkOrderNumber',
    'work_order_number',
  ]);
  const propertyLoanId = firstValueFromRecords(recordSources, [
    'propertyLoanId',
    'PropertyLoanId',
    'propertyLoanID',
    'PropertyLoanID',
    'property_loan_id',
    'cardsPropertyLoanId',
    'CardsPropertyLoanId',
    'cardsPropertyLoanID',
    'CardsPropertyLoanID',
    'cards_property_loan_id',
  ]);
  const hasPropertyLoanRecordShape = Boolean(
    firstValueFromRecords(
      [record, propertyLoanRecord],
      [
        'loanHash',
        'LoanHash',
        'loan_hash',
        'spiPropertyId',
        'SpiPropertyId',
        'SPIPropertyId',
        'sPIPropertyId',
        'sPI_property_id',
        'spi_property_id',
      ]
    ) ||
    propertyRecord ||
    propertyLoanRecord
  );
  const resolvedPropertyLoanId =
    propertyLoanId ||
    (hasPropertyLoanRecordShape
      ? firstValueFromRecords([propertyLoanRecord, record], ['id', 'Id'])
      : '');
  const propertyAddressLine1 =
    firstValueFromRecords(
      [record, propertyLoanRecord],
      [
        'propertyAddress',
        'PropertyAddress',
        'address',
        'Address',
        'address1',
        'Address1',
        'addressLine1',
        'AddressLine1',
        'address_line1',
        'address_line_1',
        'streetAddress',
        'StreetAddress',
        'street_address',
      ]
    ) ||
    firstValue(propertyRecord, [
      'address1',
      'Address1',
      'propertyAddress',
      'PropertyAddress',
      'address',
      'Address',
      'addressLine1',
      'AddressLine1',
      'address_line1',
      'address_line_1',
      'streetAddress',
      'StreetAddress',
      'street_address',
    ]);
  const propertyAddressLine2 =
    firstValueFromRecords(
      [record, propertyLoanRecord, propertyRecord],
      [
        'address2',
        'Address2',
        'addressLine2',
        'AddressLine2',
        'address_line2',
        'address_line_2',
        'streetAddress2',
        'StreetAddress2',
        'street_address2',
      ]
    ) ||
    firstValue(propertyRecord, [
      'address2',
      'Address2',
      'addressLine2',
      'AddressLine2',
      'address_line2',
      'address_line_2',
      'streetAddress2',
      'StreetAddress2',
      'street_address2',
    ]);

  return {
    ...initialData,
    orderNumber,
    propertyLoanId: resolvedPropertyLoanId,
    workOrderId:
      firstValueFromRecords(
        [workOrderRecord, record],
        ['workOrderId', 'WorkOrderId', 'work_order_id', 'workOrderID', 'WorkOrderID']
      ) ||
      firstValue(workOrderRecord, ['id', 'Id', 'ID']) ||
      firstValue(record, ['id', 'Id', 'ID']),
    spiPropertyId: firstValueFromRecords(
      [record, propertyLoanRecord, propertyRecord],
      [
        'spiPropertyId',
        'SpiPropertyId',
        'SPIPropertyId',
        'SPIPropertyID',
        'sPIPropertyId',
        'sPI_property_id',
        'spi_property_id',
        'propertyId',
        'PropertyId',
        'property_id',
      ]
    ),
    loanNumber: firstValueFromRecords(
      [record, propertyLoanRecord, propertyRecord],
      ['loanNumber', 'LoanNumber', 'loan_number', 'loanNo', 'LoanNo', 'loan_no']
    ),
    loanDisplay: firstValueFromRecords(
      [record, propertyLoanRecord, propertyRecord],
      [
        'loanNumber',
        'LoanNumber',
        'loan_number',
        'loanNo',
        'LoanNo',
        'loan_no',
        'loanDisplay',
        'LoanDisplay',
        'loan_display',
        'maskedLoanNumber',
        'MaskedLoanNumber',
        'masked_loan_number',
        'loanNumberMasked',
        'LoanNumberMasked',
        'loan_number_masked',
      ]
    ),
    loanType: normalizeLoanType(
      firstValueFromRecords(
        [record, propertyLoanRecord],
        [
          'loanType',
          'LoanType',
          'loan_type',
          'loanTypeCode',
          'LoanTypeCode',
          'loan_type_code',
          'loanTypeDescription',
          'LoanTypeDescription',
          'loan_type_description',
        ]
      )
    ),
    propertyAddress: combineAddressLines(propertyAddressLine1, propertyAddressLine2),
    city:
      firstValueFromRecords(
        [record, propertyLoanRecord],
        ['city', 'City', 'propertyCity', 'PropertyCity', 'property_city']
      ) || firstValue(propertyRecord, ['city', 'City', 'propertyCity', 'property_city']),
    state:
      firstValueFromRecords(
        [record, propertyLoanRecord],
        [
          'state',
          'State',
          'propertyState',
          'PropertyState',
          'property_state',
          'stateCode',
          'StateCode',
          'state_code',
        ]
      ) ||
      firstValue(propertyRecord, [
        'state',
        'State',
        'propertyState',
        'property_state',
        'stateCode',
        'StateCode',
        'state_code',
      ]),
    zip:
      firstValueFromRecords(
        [record, propertyLoanRecord],
        [
          'zip',
          'Zip',
          'zipCode',
          'ZipCode',
          'zip_code',
          'propertyZip',
          'PropertyZip',
          'property_zip',
          'postalCode',
          'PostalCode',
          'postal_code',
        ]
      ) ||
      firstValue(propertyRecord, [
        'zip',
        'Zip',
        'zipCode',
        'ZipCode',
        'zip_code',
        'postalCode',
        'PostalCode',
        'postal_code',
      ]),
    vendorCode: firstValueFromRecords(
      [workOrderRecord, record, vendorRecord],
      [
        'vendorCode',
        'VendorCode',
        'code',
        'Code',
        'tblVendorCode',
        'TblVendorCode',
        'tbl_vendor_code',
        'vendor_code',
        'actualSubmittingVendorCode',
        'ActualSubmittingVendorCode',
        'actual_submitting_vendor_code',
      ]
    ),
    clientLabel: firstValueFromRecords(
      [propertyLoanRecord, record, workOrderRecord, propertyRecord],
      ['clientName', 'ClientName', 'client_name', 'clientCode', 'ClientCode']
    ),
    clientCode: firstValueFromRecords(
      [propertyLoanRecord, record, workOrderRecord, propertyRecord],
      ['clientCode', 'ClientCode', 'client_code']
    ),
    investorLabel: firstValueFromRecords(
      [record, workOrderRecord, propertyLoanRecord],
      ['investorName', 'InvestorName', 'investor_name', 'investorCode', 'InvestorCode']
    ),
  };
};

const activityRecordKeys = [
  'id',
  'Id',
  'ID',
  'activityTypeCode',
  'ActivityTypeCode',
  'activity_type_code',
  'caseActivityLogId',
  'CaseActivityLogId',
  'case_activity_log_id',
  'complaintActivityLogId',
  'ComplaintActivityLogId',
  'complaint_activity_log_id',
  'note',
  'Note',
];

const findComplaintActivityRecord = (payload: unknown): FieldRecord | undefined => {
  const queue = [payload];

  while (queue.length > 0) {
    const value = queue.shift();
    if (Array.isArray(value)) {
      queue.push(...value);
      continue;
    }

    if (!isFieldRecord(value)) continue;

    if (activityRecordKeys.some((key) => textFromValue(value[key]))) {
      return value;
    }

    queue.push(
      value.activity,
      value.Activity,
      value.response,
      value.Response,
      value.data,
      value.Data
    );
  }

  return undefined;
};

export const mapComplaintActivityToEntry = (
  payload: unknown,
  lookups: ComplaintLookupSet = emptyComplaintLookups,
  fallbackId = Date.now()
): ActivityLogEntry | undefined => {
  const activity = findComplaintActivityRecord(payload);
  if (!activity) return undefined;

  const activityTypeCode = firstValue(activity, [
    'activityTypeCode',
    'ActivityTypeCode',
    'activity_type_code',
  ]);
  const directionCode = firstValue(activity, ['directionCode', 'DirectionCode', 'direction_code']);
  const metadata = parseMetadata(
    firstValue(activity, ['metadataJson', 'MetadataJson', 'metadata_json'])
  );
  const createDate = firstValue(activity, ['createDt', 'CreateDt', 'create_dt']);
  const activityId = Number(
    firstValue(activity, [
      'complaintActivityLogId',
      'ComplaintActivityLogId',
      'complaint_activity_log_id',
      'caseActivityLogId',
      'CaseActivityLogId',
      'case_activity_log_id',
      'id',
      'Id',
      'ID',
    ])
  );
  const activityTypeDisplay =
    activityTypeCode === 'NOTE' &&
    directionCode === 'OUTBOUND' &&
    textFromValue(metadata.vendorCode)
      ? 'Vendor Contact'
      : displayForLookupCode(lookups.activityTypes, activityTypeCode) ||
        displayByCode(activityTypeCode, activityTypeDisplayByCode) ||
        activityTypeCode;

  return {
    id: Number.isFinite(activityId) && activityId > 0 ? activityId : fallbackId,
    date: createDate ? createDate.replace('T', ' ').slice(0, 16) : '',
    type: activityTypeDisplay,
    user: firstValue(activity, ['createdBy', 'CreatedBy', 'created_by']) || 'System',
    note: firstValue(activity, ['note', 'Note']),
  };
};

export const mapComplaintActivityListToEntries = (
  payload: unknown,
  lookups: ComplaintLookupSet = emptyComplaintLookups
): ActivityLogEntry[] => {
  const records = Array.isArray(payload)
    ? payload
    : isFieldRecord(payload)
      ? payload.activities || payload.Activities || payload.data || payload.Data
      : undefined;

  if (!Array.isArray(records)) return [];

  return records
    .map((activity, index) => mapComplaintActivityToEntry(activity, lookups, index))
    .filter(Boolean) as ActivityLogEntry[];
};

export const getComplaintId = (payload: unknown) =>
  firstValueFromRecords(getRecords(payload), [
    'complaintId',
    'ComplaintId',
    'complaintMatterId',
    'ComplaintMatterId',
    'id',
    'Id',
    'ID',
  ]) ||
  firstValueFromRecords(
    getRecords(payload)
      .map((record) => {
        const matter = record.matter || record.Matter;
        return isFieldRecord(matter) ? matter : undefined;
      })
      .filter(Boolean),
    ['complaintId', 'ComplaintId', 'complaintMatterId', 'ComplaintMatterId', 'id', 'Id', 'ID']
  );

export const getComplaintRowVersion = (payload: unknown) =>
  firstValueFromRecords(getRecords(payload), [
    'rowVersion',
    'RowVersion',
    'row_version',
    'complaintRowVersion',
    'ComplaintRowVersion',
    'complaint_row_version',
    'complaintMatterRowVersion',
    'ComplaintMatterRowVersion',
    'complaint_matter_row_version',
    'matterRowVersion',
    'MatterRowVersion',
    'matter_row_version',
  ]) ||
  firstValueFromRecords(
    getRecords(payload)
      .map((record) => {
        const matter = record.matter || record.Matter;
        return isFieldRecord(matter) ? matter : undefined;
      })
      .filter(Boolean),
    [
      'rowVersion',
      'RowVersion',
      'row_version',
      'complaintRowVersion',
      'ComplaintRowVersion',
      'complaint_row_version',
      'complaintMatterRowVersion',
      'ComplaintMatterRowVersion',
      'complaint_matter_row_version',
      'matterRowVersion',
      'MatterRowVersion',
      'matter_row_version',
    ]
  ) ||
  firstValueFromRecords(
    getRecords(payload)
      .map((record) => {
        const caseRecord = record.case || record.Case;
        return isFieldRecord(caseRecord) ? caseRecord : undefined;
      })
      .filter(Boolean),
    [
      'rowVersion',
      'RowVersion',
      'row_version',
      'complaintRowVersion',
      'ComplaintRowVersion',
      'complaint_row_version',
      'complaintMatterRowVersion',
      'ComplaintMatterRowVersion',
      'complaint_matter_row_version',
      'matterRowVersion',
      'MatterRowVersion',
      'matter_row_version',
    ]
  );

export const buildComplaintMatterPayload = (
  formData: ComplaintIntakeData,
  resolutionData: ResolutionData,
  riskAssessment: RiskAssessment,
  caseStatus: CaseStatus,
  lookups: ComplaintLookupSet = emptyComplaintLookups
): ComplaintMatterPayload => {
  const complainantTypeCode =
    optionalText(formData.complainantTypeCode) ||
    codeForLookupDisplay(lookups.complainantTypes, formData.complainantType) ||
    codeFromDisplay(
      formData.complainantTypeCode,
      formData.complainantType,
      complainantTypeCodeByDisplay
    );
  const complaintTypeCode =
    optionalText(formData.complaintTypeCode) ||
    codeForLookupDisplay(lookups.complaintTypes, formData.complaintType) ||
    codeFromDisplay(formData.complaintTypeCode, formData.complaintType, complaintTypeCodeByDisplay);
  const contactSourceCode =
    optionalText(formData.contactSourceCode) ||
    codeForLookupDisplay(lookups.contactSources, formData.contactSource) ||
    codeFromDisplay(formData.contactSourceCode, formData.contactSource, contactSourceCodeByDisplay);
  const departmentCode =
    optionalText(formData.departmentCode) ||
    codeForLookupDisplay(lookups.departments, formData.department) ||
    codeFromDisplay(formData.departmentCode, formData.department, departmentCodeByDisplay);
  const severityCode =
    optionalText(formData.severityCode) ||
    codeForLookupDisplay(lookups.severities, formData.severity) ||
    codeFromDisplay(formData.severityCode, formData.severity, severityCodeByDisplay);
  const caseStatusCode =
    codeForLookupDisplay(lookups.caseStatuses, caseStatus) ||
    codeFromDisplay('', caseStatus, caseStatusCodeByDisplay);
  const riskLevelCode =
    codeForLookupDisplay(lookups.riskLevels, riskAssessment.level) ||
    codeFromDisplay('', riskAssessment.level, riskLevelCodeByDisplay);
  const complainantContactPhone = optionalText(formData.complainantContactPhone);
  const complainantContactEmail = optionalText(formData.complainantContactEmail);

  return {
    propertyLoanId: optionalId(formData.propertyLoanId),
    spiPropertyId: optionalId(formData.spiPropertyId),
    orderNumber: optionalId(formData.orderNumber),
    workOrderId: optionalId(formData.workOrderId),
    complainantFirst: formData.complainantFirst.trim(),
    complainantLast: formData.complainantLast.trim(),
    complainantTypeCode,
    complainantContactEmail,
    complainantContactPhone,
    departmentCode: optionalText(departmentCode),
    severityCode: optionalText(severityCode),
    complaintTypeCode,
    contactSourceCode,
    receivedDate: toProtobufTimestamp(formData.receivedDate),
    description: formData.description.trim(),
    demand: optionalText(formData.demand),
    assignedToUser: optionalText(formData.assignedTo),
    vendorCode: optionalText(formData.vendorCode),
    caseStatusCode,
    riskLevelCode,
    riskIndicators: riskAssessment.indicators,
    followUpDate: toProtobufTimestamp(resolutionData.followUpDate),
    actionSteps: optionalText(resolutionData.actionSteps),
    resolutionDetails: optionalText(resolutionData.resolutionDetails),
  };
};

export const buildComplaintMatterSnapshot = (
  formData: ComplaintIntakeData,
  resolutionData: ResolutionData,
  riskAssessment: RiskAssessment,
  caseStatus: CaseStatus,
  lookups: ComplaintLookupSet = emptyComplaintLookups
) =>
  JSON.stringify({
    matter: buildComplaintMatterPayload(
      formData,
      resolutionData,
      riskAssessment,
      caseStatus,
      lookups
    ),
    counselinkId: optionalText(formData.counselinkId),
    complaintTypeCodes: formData.complaintTypeCodes?.length
      ? formData.complaintTypeCodes
      : [formData.complaintTypeCode].filter(Boolean),
  });

export const buildCreateComplaintMatterRequest = (
  formData: ComplaintIntakeData,
  resolutionData: ResolutionData,
  riskAssessment: RiskAssessment,
  caseStatus: CaseStatus,
  lookups: ComplaintLookupSet = emptyComplaintLookups
): CreateComplaintMatterRequest => ({
  matter: buildComplaintMatterPayload(
    formData,
    resolutionData,
    riskAssessment,
    caseStatus,
    lookups
  ),
});

export const buildUpdateComplaintMatterRequest = (
  formData: ComplaintIntakeData,
  resolutionData: ResolutionData,
  riskAssessment: RiskAssessment,
  caseStatus: CaseStatus,
  lookups: ComplaintLookupSet = emptyComplaintLookups
): UpdateComplaintMatterRequest => ({
  rowVersion: formData.rowVersion.trim(),
  matter: buildComplaintMatterPayload(
    formData,
    resolutionData,
    riskAssessment,
    caseStatus,
    lookups
  ),
});

const getNestedRecord = (record: FieldRecord, key: string) => {
  const value = record[key];
  return isFieldRecord(value) ? value : undefined;
};

export const getRequestFailureMessage = (
  error: unknown,
  operation: 'activity' | 'assignment' | 'lookup' | 'lookups' | 'routing rules' | 'sync' | 'case'
) => {
  const errorRecord = isFieldRecord(error) ? error : {};
  const response = getNestedRecord(errorRecord, 'response');
  const status = Number(response?.status);
  const code = textFromValue(errorRecord.code);
  const message = textFromValue(errorRecord.message);
  const isSync = operation === 'sync';
  const isAssignment = operation === 'assignment';
  const isCase = operation === 'case';
  const isActivity = operation === 'activity';
  const isLookups = operation === 'lookup' || operation === 'lookups';

  if (!status && (code === 'ERR_NETWORK' || message === 'Network Error')) {
    return 'Unable to reach SafeView services. Confirm dev network access and try again.';
  }

  if (!status && message) {
    return message;
  }

  if (status === 401) {
    return 'SafeView session expired. Sign in again and retry.';
  }

  if (status === 403) {
    return 'You do not have permission to complete this SafeView action.';
  }

  if (status === 404) {
    if (isAssignment) {
      return 'Complaint assignment is not available in this environment.';
    }
    if (isSync) return 'Complaint sync is not available in this environment.';
    if (isActivity) {
      return 'Complaint activity logging is not available in this environment.';
    }
    if (isLookups) {
      return 'Complaint lookup values are not available in this environment.';
    }
    if (isCase) return 'No complaint case was returned for this route context.';

    return 'No SafeView record was returned for this lookup.';
  }

  if (status === 400) {
    if (isAssignment) {
      return 'SafeView rejected the complaint assignment. Refresh and retry.';
    }
    if (isSync) {
      return 'SafeView rejected the complaint data. Review required fields and retry.';
    }
    if (isActivity) {
      return 'SafeView rejected the complaint activity. Review the activity details and retry.';
    }
    if (isLookups) return 'SafeView rejected the complaint lookup request.';
    if (isCase) return 'SafeView rejected the complaint case request.';

    return 'SafeView rejected the lookup request.';
  }

  if (status >= 500) {
    if (isCase) return 'Unable to load the complaint case. Retry after the service recovers.';

    return 'SafeView services are unavailable. Retry after the service recovers.';
  }

  if (isSync) return 'Unable to sync the complaint to SafeView.';
  if (isAssignment) return 'Unable to assign the complaint.';
  if (isActivity) return 'Unable to log complaint activity.';
  if (isLookups) return 'Unable to load complaint lookup values.';
  if (isCase) return 'Unable to load the complaint case.';

  return 'Unable to load the SafeView record.';
};
